import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { DndContext, useDraggable, useDroppable, type DragEndEvent } from '@dnd-kit/core';
import { Loader2, Play, Rocket, SquareCheckBig } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, PRIORITY_COLOURS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { useDragSensors } from '@/lib/dndSensors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError, api } from '@/lib/api';
import { formatMinutes } from '@/lib/utils';
import { useCan } from '@/features/auth';
import { useProject } from '@/features/projects';
import { useBulkTasks, useTasks, type TaskView } from '@/features/tasks/api';
import { DesktopOnly } from './DesktopOnly';
import { useSprintCapacity } from './api';

interface SprintRecord {
  _id: string;
  name: string;
  goal?: string;
  status: 'planned' | 'active' | 'completed';
  startDate: string;
  endDate: string;
}

function TaskChip({ task }: { task: TaskView }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task._id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="vt-surface relative cursor-grab px-2 py-1.5 text-[12px] active:cursor-grabbing"
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <span className="vt-spine" style={{ background: PRIORITY_COLOURS[task.priority] }} aria-hidden />
      <div className="pl-1.5">
        <span className="vt-mono text-[10px] vt-muted">{task.key}</span>
        <div className="truncate">{task.title}</div>
        {task.estimateMinutes ? (
          <span className="vt-mono text-[10px] vt-muted">{formatMinutes(task.estimateMinutes)}</span>
        ) : (
          <span className="text-[10px] text-[var(--warn)]">no estimate</span>
        )}
      </div>
    </div>
  );
}

function Pane({
  id,
  title,
  subtitle,
  tasks,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  tasks: TaskView[];
  children?: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <section
      ref={setNodeRef}
      className="flex min-h-[420px] flex-1 flex-col rounded-[6px] bg-[var(--surface-2)] p-2"
      style={{ outline: isOver ? '2px solid var(--accent)' : undefined }}
    >
      <header className="mb-2 px-1">
        <h2 className="text-[13px] font-semibold">{title}</h2>
        {subtitle ? <p className="text-[11px] vt-muted">{subtitle}</p> : null}
        {children}
      </header>
      <div className="flex flex-col gap-1.5 overflow-y-auto">
        {tasks.map((task) => (
          <TaskChip key={task._id} task={task} />
        ))}
        {tasks.length === 0 && <p className="px-1 py-4 text-center text-[11px] vt-muted">Empty</p>}
      </div>
    </section>
  );
}

export function SprintPlanningPage() {
  const { key = '' } = useParams();
  const can = useCan();
  const { data: project } = useProject(key);
  const bulk = useBulkTasks();
  const [selectedSprintId, setSelectedSprintId] = useState<string>();

  const { data: sprints, isLoading: sprintsLoading, refetch } = useQuery({
    queryKey: ['sprints', project?._id],
    queryFn: async () => (await api.get<SprintRecord[]>(`/projects/${project!._id}/sprints`)).data,
    enabled: Boolean(project),
  });

  const sprint = useMemo(
    () =>
      sprints?.find((s) => s._id === selectedSprintId) ??
      sprints?.find((s) => s.status === 'active') ??
      sprints?.find((s) => s.status === 'planned'),
    [sprints, selectedSprintId],
  );

  const { data: backlogData } = useTasks(
    { projectId: project?._id, sprintId: 'backlog', statusCategory: 'todo,in_progress', limit: 200 },
    { enabled: Boolean(project) },
  );
  const { data: sprintData } = useTasks(
    { projectId: project?._id, sprintId: sprint?._id, limit: 200 },
    { enabled: Boolean(sprint) },
  );
  const { data: capacity } = useSprintCapacity(sprint?._id);

  const backlog = (backlogData?.items ?? []) as TaskView[];
  const inSprint = (sprintData?.items ?? []) as TaskView[];

  function move(taskId: string, toSprint: boolean) {
    bulk
      .mutateAsync({
        taskIds: [taskId],
        op: 'move-to-sprint',
        payload: { sprintId: toSprint ? sprint?._id : null },
      })
      .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Could not move'));
  }

  // V10 §7 — the shared sensors, so a keystroke inside an editable element
  // can never lift a draggable. See `lib/dndSensors.ts`.
  const sensors = useDragSensors();
  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    if (over.id === 'sprint') move(String(active.id), true);
    if (over.id === 'backlog') move(String(active.id), false);
  }

  async function startSprint() {
    try {
      await api.post(`/sprints/${sprint!._id}/start`);
      toast.success(`${sprint!.name} started`);
      await refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not start the sprint');
    }
  }

  async function closeSprint(incompleteTo: 'next' | 'backlog') {
    try {
      const result = await api.post<{ moved: number }>(`/sprints/${sprint!._id}/close`, {
        incompleteTo,
      });
      toast.success(
        `${sprint!.name} closed — ${result.data.moved} unfinished ${result.data.moved === 1 ? 'task' : 'tasks'} moved to ${incompleteTo}`,
      );
      await refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not close the sprint');
    }
  }

  const committed = capacity?.committedMinutes ?? 0;
  const available = capacity?.capacityMinutes ?? 0;
  const percentage = available === 0 ? 0 : Math.round((committed / available) * 100);
  const canManage = can(PERMISSIONS.SPRINT_MANAGE);

  return (
    <DesktopOnly title="Sprint planning" listHref={`/projects/${key}/list`}>
      <PageHeader
        title={`${project?.icon ?? ''} Sprint planning`}
        subtitle={sprint ? `${sprint.name} · ${sprint.status}` : 'No sprint yet'}
        actions={
          <>
            {sprints && sprints.length > 0 && (
              <select
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={sprint?._id ?? ''}
                aria-label="Choose a sprint"
                onChange={(e) => setSelectedSprintId(e.target.value)}
              >
                {sprints.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.status})
                  </option>
                ))}
              </select>
            )}
            {canManage && sprint?.status === 'planned' && (
              <Button variant="primary" size="sm" onClick={() => void startSprint()}>
                <Play />
                Start sprint
              </Button>
            )}
            {canManage && sprint?.status === 'active' && (
              <Button variant="secondary" size="sm" onClick={() => void closeSprint('next')}>
                <SquareCheckBig />
                Close sprint
              </Button>
            )}
          </>
        }
      />

      <div className="p-4">
        {sprintsLoading ? (
          <Skeleton className="h-[50vh]" />
        ) : !sprint ? (
          <EmptyState
            icon={<Rocket className="size-6" />}
            title="No sprints yet"
            description="Create a sprint from the project's Sprints tab to start planning."
          />
        ) : (
          <>
            <div className="vt-surface mb-3 p-3">
              <div className="mb-1.5 flex items-baseline justify-between text-[12px]">
                <span className="font-medium">Committed vs capacity</span>
                <span className="vt-mono">
                  {formatMinutes(committed)} / {formatMinutes(available)}
                  <span
                    className="ml-1.5 font-medium"
                    style={{ color: percentage > 100 ? 'var(--danger)' : 'var(--accent)' }}
                  >
                    {percentage}%
                  </span>
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[var(--surface-2)]">
                <div
                  className="h-full rounded-full transition-[width] duration-[180ms]"
                  style={{
                    width: `${Math.min(percentage, 100)}%`,
                    background: percentage > 100 ? 'var(--danger)' : 'var(--accent)',
                  }}
                />
              </div>
              <p className="mt-1.5 text-[11px] vt-muted">
                Capacity is the team's weekly hours pro-rated to {capacity?.workingDays ?? 0} working
                days, minus approved leave and holidays.
                {capacity?.members.some((m) => m.leaveDays > 0) &&
                  ` On leave: ${capacity.members
                    .filter((m) => m.leaveDays > 0)
                    .map((m) => `${m.name} (${m.leaveDays}d)`)
                    .join(', ')}.`}
              </p>
            </div>

            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <div className="flex gap-3">
                <Pane
                  id="backlog"
                  title="Backlog"
                  subtitle={`${backlog.length} tasks not in a sprint`}
                  tasks={backlog}
                />
                <Pane
                  id="sprint"
                  title={sprint.name}
                  subtitle={`${inSprint.length} committed`}
                  tasks={inSprint}
                >
                  {bulk.isPending && <Loader2 className="mt-1 size-3.5 animate-spin vt-muted" />}
                </Pane>
              </div>
            </DndContext>
          </>
        )}
      </div>
    </DesktopOnly>
  );
}
